import React, { useEffect, useState } from 'react';
import './styles.scss';

interface MailMessage {
  id: string;
  fromName: string;
  text: string;
  sentAt: number;
  goldAttached?: number;
  goldClaimed?: boolean;
  seal?: { fromName: string; blazonId?: string; broken: boolean; brokenAt?: number };
}

interface Blazon {
  id: string;
  name: string;
  motto: string;
  primary: string;
  secondary: string;
}

// The seal drawn from the two colours of a blazon (a plain red wax one without a blazon)
const SealMark: React.FC<{ blazon: Blazon | null; broken?: boolean }> = ({ blazon, broken }) => (
  <span
    className={`mailbox-seal${broken ? ' mailbox-seal--broken' : ''}`}
    style={{
      background: blazon ? `radial-gradient(circle at 35% 35%, ${blazon.secondary} 0 18%, ${blazon.primary} 20%)` : '#8b1a1a',
      borderColor: blazon ? blazon.secondary : '#d4af37',
    }}
    aria-hidden="true"
  />
);

type View = 'menu' | 'read' | 'compose';

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('mailbox sendMessage', payload);
  }
};

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Mirrors missiveTravel.ts's formatDuration on the server - kept separate
// since the server only ever sends a raw timestamp (nextArrivalAt), not a
// preformatted string, so this needs to live client-side too.
function formatEta(ms: number): string {
  const totalMinutes = Math.max(1, Math.round(ms / 60_000));
  if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes > 1 ? 's' : ''}`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h${String(minutes).padStart(2, '0')}`;
}

const Mailbox: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<View>('menu');
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [nextArrivalAt, setNextArrivalAt] = useState<number | null>(null);
  const [toName, setToName] = useState('');
  const [text, setText] = useState('');
  const [goldAmount, setGoldAmount] = useState('');
  const [knownNames, setKnownNames] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [blazons, setBlazons] = useState<Blazon[]>([]);
  const [sealBlazon, setSealBlazon] = useState<Blazon | null>(null);
  const [sealIt, setSealIt] = useState(false);
  const [express, setExpress] = useState(false);

  useEffect(() => {
    const showHandler = () => {
      setView('menu');
      setMessages([]);
      setPendingCount(0);
      setNextArrivalAt(null);
      setToName('');
      setText('');
      setGoldAmount('');
      setSealIt(false);
      setExpress(false);
      setVisible(true);
    };
    const showReadHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { messages?: MailMessage[]; pendingCount?: number; nextArrivalAt?: number | null; blazons?: Blazon[] } | null;
      setMessages(detail?.messages ?? []);
      setBlazons(detail?.blazons ?? []);
      setPendingCount(detail?.pendingCount ?? 0);
      setNextArrivalAt(detail?.nextArrivalAt ?? null);
      setView('read');
    };
    const hideHandler = () => setVisible(false);
    const namesHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { names?: string[]; sealBlazon?: Blazon | null } | null;
      setKnownNames(detail?.names ?? []);
      setSealBlazon(detail?.sealBlazon ?? null);
    };

    window.addEventListener('skymp5-client:showMailbox', showHandler);
    window.addEventListener('skymp5-client:showMailboxRead', showReadHandler);
    window.addEventListener('skymp5-client:hideMailbox', hideHandler);
    window.addEventListener('skymp5-client:mailboxNamesList', namesHandler);
    return () => {
      window.removeEventListener('skymp5-client:showMailbox', showHandler);
      window.removeEventListener('skymp5-client:showMailboxRead', showReadHandler);
      window.removeEventListener('skymp5-client:hideMailbox', hideHandler);
      window.removeEventListener('skymp5-client:mailboxNamesList', namesHandler);
    };
  }, []);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  // Custom-rendered instead of a native <datalist>: SkyrimPlatform's CEF
  // surface renders off-screen into the game's own DirectX swapchain, and
  // Chromium's native suggestion-box popup (like <select>'s dropdown) relies
  // on a platform popup window that off-screen rendering doesn't capture -
  // <datalist> showed literally nothing in-game despite working in a normal
  // browser. Plain DOM list items have no such dependency.
  const nameSuggestions = (() => {
    const query = toName.trim().toLowerCase();
    if (!query) return [];
    return knownNames
      .filter((name) => name.toLowerCase().includes(query) && name.toLowerCase() !== query)
      .slice(0, 6);
  })();

  const pickSuggestion = (name: string) => {
    setToName(name);
    setShowSuggestions(false);
  };

  const openRead = () => send({ action: 'requestRead' });
  const openCompose = () => {
    setToName('');
    setText('');
    setGoldAmount('');
    setSealIt(false);
    setExpress(false);
    setView('compose');
  };

  const deleteMessage = (id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    send({ action: 'delete', id });
  };

  const breakSeal = (id: string) => send({ action: 'breakSeal', id });

  const claimGold = (id: string) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, goldClaimed: true } : m)));
    send({ action: 'claimGold', id });
  };

  const submitSend = () => {
    if (!toName.trim() || !text.trim()) return;
    const parsedGold = Math.floor(Number(goldAmount));
    const goldToSend = Number.isFinite(parsedGold) && parsedGold > 0 ? parsedGold : 0;
    send({ action: 'send', toName: toName.trim(), text: text.trim(), goldAmount: goldToSend, sealed: sealIt, express });
    setView('menu');
  };

  return (
    <div className="mailbox-overlay">
      <div className="mailbox-panel">
        <div className="mailbox-header">
          <div className="mailbox-header-title">Boîte aux lettres</div>
        </div>

        {view === 'menu' && (
          <div className="mailbox-body mailbox-body--menu">
            <button type="button" className="mailbox-tile" onClick={openRead}>
              <span className="mailbox-tile-label">Relever le courrier</span>
            </button>
            <button type="button" className="mailbox-tile" onClick={openCompose}>
              <span className="mailbox-tile-label">Écrire une lettre</span>
            </button>
          </div>
        )}

        {view === 'read' && (
          <div className="mailbox-body">
            <div className="mailbox-list">
              {messages.length === 0 ? (
                <div className="mailbox-empty">Aucun courrier.</div>
              ) : (
                messages.map((m) => (
                  <div className="mailbox-letter" key={m.id}>
                    <div className="mailbox-letter-head">
                      <span className="mailbox-letter-from">De : {m.fromName}</span>
                      <span className="mailbox-letter-date">{formatDate(m.sentAt)}</span>
                      <button
                        type="button"
                        className="mailbox-icon-btn"
                        title="Supprimer" aria-label="Supprimer"
                        onClick={() => deleteMessage(m.id)}
                      >
                        ×
                      </button>
                    </div>
                    {m.seal && !m.seal.broken ? (
                      <div className="mailbox-letter-sealed">
                        <SealMark blazon={blazons.find((b) => b.id === m.seal?.blazonId) ?? null} />
                        <span className="mailbox-letter-sealed-text">
                          Lettre scellée par {m.seal.fromName}
                          {blazons.find((b) => b.id === m.seal?.blazonId) ? ` (${blazons.find((b) => b.id === m.seal?.blazonId)?.name})` : ''}
                        </span>
                        <button type="button" className="mailbox-button" onClick={() => breakSeal(m.id)}>
                          Briser le sceau
                        </button>
                      </div>
                    ) : (
                      <>
                        {m.seal && (
                          <div className="mailbox-letter-seal-broken">
                            <SealMark blazon={blazons.find((b) => b.id === m.seal?.blazonId) ?? null} broken />
                            Sceau brisé{m.seal.brokenAt ? ` le ${formatDate(m.seal.brokenAt)}` : ''}
                          </div>
                        )}
                        <p className="mailbox-letter-text">{m.text}</p>
                      </>
                    )}
                    {m.goldAttached !== undefined && m.goldAttached > 0 && (
                      m.goldClaimed ? (
                        <div className="mailbox-letter-gold mailbox-letter-gold--claimed">Or récupéré</div>
                      ) : (
                        <div className="mailbox-letter-gold">
                          <span className="mailbox-letter-gold-amount">{m.goldAttached} Septims joints</span>
                          <button
                            type="button"
                            className="mailbox-button mailbox-button--gold"
                            onClick={() => claimGold(m.id)}
                          >
                            Récupérer l'or
                          </button>
                        </div>
                      )
                    )}
                  </div>
                ))
              )}
            </div>
            {pendingCount > 0 && (
              <div className="mailbox-pending-hint">
                {pendingCount} lettre{pendingCount > 1 ? 's' : ''} en chemin
                {nextArrivalAt !== null && `, prochaine dans ${formatEta(nextArrivalAt - Date.now())}`}
              </div>
            )}
            <div className="mailbox-footer">
              <button type="button" className="mailbox-button" onClick={openCompose}>
                Écrire une lettre
              </button>
            </div>
          </div>
        )}

        {view === 'compose' && (
          <div className="mailbox-body">
            <label className="mailbox-field">
              <span className="mailbox-field-label">Destinataire</span>
              <div className="mailbox-autocomplete">
                <input
                  className="mailbox-input"
                  type="text"
                  value={toName}
                  onChange={(e) => { setToName(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Nom du personnage"
                  maxLength={64}
                  autoComplete="off"
                />
                {showSuggestions && nameSuggestions.length > 0 && (
                  <ul className="mailbox-autocomplete-list">
                    {nameSuggestions.map((name) => (
                      <li
                        key={name}
                        className="mailbox-autocomplete-item"
                        // onMouseDown (not onClick) fires before the input's
                        // onBlur, so the pick registers before the list hides.
                        onMouseDown={() => pickSuggestion(name)}
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </label>
            <label className="mailbox-field">
              <span className="mailbox-field-label">Message</span>
              <textarea
                className="mailbox-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
                placeholder="Ta lettre..."
              />
            </label>
            <label className="mailbox-field">
              <span className="mailbox-field-label">Or joint (optionnel)</span>
              <input
                className="mailbox-input"
                type="number"
                min={0}
                step={1}
                value={goldAmount}
                onChange={(e) => setGoldAmount(e.target.value)}
                placeholder="0"
              />
            </label>
            <label className="mailbox-field mailbox-field--check">
              <input type="checkbox" checked={sealIt} onChange={(e) => setSealIt(e.target.checked)} />
              <SealMark blazon={sealBlazon} />
              <span className="mailbox-field-label">
                Sceller la lettre{sealBlazon ? ` (sceau : ${sealBlazon.name})` : ''} : le destinataire devra briser le sceau pour la lire
              </span>
            </label>
            <label className="mailbox-field mailbox-field--check">
              <input type="checkbox" checked={express} onChange={(e) => setExpress(e.target.checked)} />
              <span className="mailbox-field-label">
                Envoi express (+200 Septims) : la lettre est disponible en 5 minutes dans toutes les boîtes
              </span>
            </label>
            <div className="mailbox-compose-hint">
              {express
                ? 'Frais : 201 Septims. Sans express, la lettre arrive d\'abord au hold où le destinataire est citoyen, puis plus tard dans les autres.'
                : "Frais d'affranchissement : 1 Septim. La lettre arrive d'abord au hold où le destinataire est citoyen, puis plus tard dans les autres villes."}
            </div>
            <div className="mailbox-footer">
              <button type="button" className="mailbox-button mailbox-button--ghost" onClick={() => setView('menu')}>
                Annuler
              </button>
              <button
                type="button"
                className="mailbox-button"
                disabled={!toName.trim() || !text.trim()}
                onClick={submitSend}
              >
                Envoyer
              </button>
            </div>
          </div>
        )}

        <div className="mailbox-close-row">
          <div className="mailbox-hint">
            <kbd>Échap</kbd> Fermer
          </div>
          <button type="button" className="mailbox-button mailbox-button--quiet" onClick={close}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default Mailbox;
